import type { MTSectionFormConfig } from '@/multi-territory/mt-types/mt-section-form-config'

/**
 * Mock form config for India (IN) — Section Master "Add Section" form.
 * In production, this comes from: GET /api/v1/section-master/form-config?country_code=IN
 */
export const INDIA_SECTION_FORM_CONFIG: MTSectionFormConfig = {
  form_title: 'Add Section',
  country_code: 'IN',
  country_name: 'India',
  fields: [
    {
      field_key: 'section_code',
      label: 'Section Code',
      type: 'dropdown',
      required: true,
      placeholder: 'Section Code',
      options_api: '/api/v1/section-codes?country=IN',
      options: [
        { label: '393(3)-1 [old:194B]', value: '393(3)-1 [old:194B]' },
        { label: 'No TDS', value: 'No TDS' },
        { label: '393(1)-6(i)D [old:194C]', value: '393(1)-6(i)D [old:194C]' },
        { label: '393(1)-8(iv) [old:194R]', value: '393(1)-8(iv) [old:194R]' },
        { label: '393(1)-7 [old:194]', value: '393(1)-7 [old:194]' },
        { label: '393(1)-8(ii) [old:194Q]', value: '393(1)-8(ii) [old:194Q]' },
        { label: '393(3)-2 [old:194BA]', value: '393(3)-2 [old:194BA]' },
        { label: '393(1)-8(v) [old:194O]', value: '393(1)-8(v) [old:194O]' },
        {
          label: '393(1)-6(iii)D(a) [old:194JA]',
          value: '393(1)-6(iii)D(a) [old:194JA]',
        },
        { label: '393(1)-5(i) [old:193]', value: '393(1)-5(i) [old:193]' },
        {
          label: '393(1)-5(iii) [old:194A]',
          value: '393(1)-5(iii) [old:194A]',
        },
        {
          label: '393(1)-2(ii)D(b) [old:194IB]',
          value: '393(1)-2(ii)D(b) [old:194IB]',
        },
        { label: '393(2)-17 [old:195]', value: '393(2)-17 [old:195]' },
        { label: '393(1)-8(vi) [old:194S]', value: '393(1)-8(vi) [old:194S]' },
        {
          label: '393(1)-6(iii)D(b) [old:194JB]',
          value: '393(1)-6(iii)D(b) [old:194JB]',
        },
        {
          label: '393(1)-1(ii) [old:194H]',
          value: '393(1)-1(ii) [old:194H]',
        },
        {
          label: '393(1)-2(ii)D(a) [old:194IA]',
          value: '393(1)-2(ii)D(a) [old:194IA]',
        },
        { label: '393(3)-3 [old:194BB]', value: '393(3)-3 [old:194BB]' },
      ],
      grid_column: 1,
      order: 1,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Section Code is required',
      },
    },
    {
      field_key: 'broad_head',
      label: 'Broad Head',
      type: 'dropdown',
      required: true,
      placeholder: 'Enter broad head description',
      grid_column: 2,
      order: 2,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: {
        source: 'section_code',
        api: '/api/v1/section-broadhead?country=IN&section_code={section_code}',
        response_format: 'key_value_map',
        field_path: 'data',
        clear_on_change: true,
        auto_select_single: true,
      },
      validations: {
        required_message: 'Broad Head is required',
      },
    },
    {
      field_key: 'start_date',
      label: 'Start Date',
      type: 'date',
      required: true,
      placeholder: 'dd/mm/yyyy',
      date_format: 'DD/MM/YYYY',
      grid_column: 1,
      order: 3,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Start Date is required',
        max_date: 'end_date',
      },
    },
    {
      field_key: 'end_date',
      label: 'End Date',
      type: 'date',
      required: false,
      placeholder: 'dd/mm/yyyy',
      date_format: 'DD/MM/YYYY',
      grid_column: 2,
      order: 4,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        min_date: 'start_date',
      },
    },
    {
      field_key: 'applicable_to',
      label: 'Applicable To',
      type: 'dropdown',
      required: true,
      placeholder: 'Select Applicable To',
      options: [
        { label: 'Individual/HUF', value: 'individual_huf' },
        { label: 'Company', value: 'company' },
        { label: 'All', value: 'all' },
      ],
      grid_column: 1,
      order: 5,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Applicable To is required',
      },
    },
    {
      field_key: 'payee_status',
      label: 'Payee Status',
      type: 'dropdown',
      required: true,
      placeholder: 'Select Payee Status',
      options: [
        { label: 'Resident', value: 'resident' },
        { label: 'Non-Resident', value: 'non_resident' },
        { label: 'Both', value: 'both' },
      ],
      grid_column: 2,
      order: 6,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Payee Status is required',
      },
    },
    {
      field_key: 'threshold_limit_annual',
      label: 'Threshold Limit (Annual)',
      type: 'number',
      required: true,
      placeholder: 'Enter amount',
      grid_column: 1,
      order: 7,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Threshold Limit (Annual) is required',
        min: 0,
        regex: '^[0-9]+(\\.[0-9]{1,2})?$',
        regex_message: 'Enter a valid amount',
      },
    },
    {
      field_key: 'threshold_limit_transaction',
      label: 'Threshold Limit (Transaction)',
      type: 'number',
      required: true,
      placeholder: 'Enter amount',
      grid_column: 2,
      order: 8,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Threshold Limit (Transaction) is required',
        min: 0,
        regex: '^[0-9]+(\\.[0-9]{1,2})?$',
        regex_message: 'Enter a valid amount',
      },
    },
    {
      field_key: 'prescribed_rate',
      label: 'Prescribed Rate (%)',
      type: 'number',
      required: true,
      placeholder: 'Enter rate',
      grid_column: 1,
      order: 9,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Prescribed Rate is required',
        min: 0,
        max: 100,
        regex: '^[0-9]+(\\.[0-9]{1,2})?$',
        regex_message: 'Enter a valid percentage',
      },
    },
  ],
}

/**
 * Mock form config for Singapore (SG) — Section Master "Add nature of service" form.
 * In production, this comes from: GET /api/v1/section-master/form-config?country_code=SG
 */
export const SINGAPORE_SECTION_FORM_CONFIG: MTSectionFormConfig = {
  form_title: 'Add nature of service',
  country_code: 'SG',
  country_name: 'Singapore',
  fields: [
    {
      field_key: 'nature_of_service',
      label: 'Nature of service',
      type: 'dropdown',
      required: true,
      placeholder: 'Select nature of service',
      options_api: '/api/v1/nature-of-service?country=SG',
      options: [
        { label: 'Interest from loan or indebtedness', value: 'interest_loan' },
        { label: 'Royalty or payment for use of IP', value: 'royalty_ip' },
        { label: 'Technical service fees', value: 'technical_service' },
        { label: 'Management fees', value: 'management_fees' },
        { label: 'Rental of movable property', value: 'rental_movable' },
      ],
      grid_column: 1,
      order: 1,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Nature of service is required',
      },
    },
    {
      field_key: 'form_type',
      label: 'Form type',
      type: 'dropdown',
      required: true,
      placeholder: 'Select form type',
      options: [
        { label: 'IR37', value: 'IR37' },
        { label: 'IR21', value: 'IR21' },
      ],
      grid_column: 2,
      order: 2,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Form type is required',
      },
    },
    {
      field_key: 'start_date',
      label: 'Start date',
      type: 'date',
      required: true,
      placeholder: 'Select',
      date_format: 'DD/MM/YYYY',
      grid_column: 1,
      order: 3,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Start date is required',
        max_date: 'end_date',
      },
    },
    {
      field_key: 'end_date',
      label: 'End date',
      type: 'date',
      required: false,
      placeholder: 'Select',
      date_format: 'DD/MM/YYYY',
      grid_column: 2,
      order: 4,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        min_date: 'start_date',
      },
    },
    {
      field_key: 'applicable_to',
      label: 'Applicable to',
      type: 'dropdown',
      required: true,
      placeholder: 'Select Applicable to',
      options: [
        { label: 'Non-resident', value: 'non_resident' },
        { label: 'Resident', value: 'resident' },
      ],
      grid_column: 1,
      order: 5,
      editable: false,
      visible: true,
      auto_fill: {
        source: null,
        api: null,
        field_path: null,
        trigger: 'onLoad',
        default_value: 'non_resident',
      },
      depends_on: null,
      validations: {
        required_message: 'Applicable to is required',
      },
    },
    {
      field_key: 'place_of_operation',
      label: 'Place of operation',
      type: 'dropdown',
      required: true,
      placeholder: 'Select place of operation',
      options: [
        { label: 'Outside Singapore', value: 'outside_singapore' },
        { label: 'Inside Singapore', value: 'inside_singapore' },
      ],
      grid_column: 2,
      order: 6,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Place of operation is required',
      },
    },
    {
      field_key: 'payee_status',
      label: 'Payee status',
      type: 'dropdown',
      required: true,
      placeholder: 'Select payee status',
      options: [
        { label: 'Individual', value: 'individual' },
        { label: 'Corporate', value: 'corporate' },
      ],
      grid_column: 1,
      order: 7,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Payee status is required',
      },
    },
    {
      field_key: 'prescribed_rate',
      label: 'Prescribed rate(%)',
      type: 'number',
      required: true,
      placeholder: 'Enter rate',
      grid_column: 2,
      order: 8,
      editable: true,
      visible: true,
      auto_fill: null,
      depends_on: null,
      validations: {
        required_message: 'Prescribed rate is required',
        min: 0,
        max: 100,
        regex: '^[0-9]+(\\.[0-9]{1,2})?$',
        regex_message: 'Enter a valid percentage',
      },
    },
  ],
}

/**
 * Map of country_code -> form config for testing.
 * In production, this is replaced by the API call.
 */
export const SECTION_FORM_CONFIG_MAP: Record<string, MTSectionFormConfig> = {
  IN: INDIA_SECTION_FORM_CONFIG,
  SG: SINGAPORE_SECTION_FORM_CONFIG,
}
